// Admin console: E-Learning, the video groups (GET and PUT /learning/admin/courses; a course in the API).
//   header   plain spacing, hairline below  h1, count, Tambah kelompok
//   notice   notice                         the last change saved, or why it was not
//   groups   rows                           title, peran, video count; Naik, Turun, Ubah, Hapus (confirm)
// Every change on this screen (order, delete) saves the whole list at once, as the API takes it.
// The editor for one group lives at /admin/elearning/baru and /admin/elearning/:courseId.
import { useState } from "react";
import * as API from "../api";
import { formatCount } from "../lib/format";
import { ROLE_LABEL } from "../lib/labels";
import { Badge, Dialog, EmptyState, Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

export const ADMIN_LEARNING = "/admin/elearning";

export function adminCourseHref(id?: string): string {
  return id ? `${ADMIN_LEARNING}/${encodeURIComponent(id)}` : `${ADMIN_LEARNING}/baru`;
}

export function RoleBadges({ roles }: { roles: API.LearningRole[] }) {
  return (
    <span className="role-badges">
      {roles.map((r) => (
        <Badge key={r}>{ROLE_LABEL[r]}</Badge>
      ))}
    </span>
  );
}

type Flash = { tone: "success" | "danger"; title: string; text: string } | null;

export function ElearningAdminScreen() {
  useTitle("E-Learning");
  const list = useAsync(() => API.listAdminCourses(), []);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<Flash>(null);
  const [deleting, setDeleting] = useState<API.AdminCourse | null>(null);
  const courses = list.data ?? [];

  async function persist(next: API.AdminCourse[], done: string, text: string): Promise<boolean> {
    setBusy(true);
    setFlash(null);
    try {
      list.set(await API.saveAdminCourses(next));
      setFlash({ tone: "success", title: done, text });
      return true;
    } catch (err) {
      setFlash({ tone: "danger", title: "Perubahan belum disimpan", text: API.errorMessage(err) });
      return false;
    } finally {
      setBusy(false);
    }
  }

  function move(i: number, by: -1 | 1) {
    const j = i + by;
    if (j < 0 || j >= courses.length) return;
    const next = [...courses];
    [next[i], next[j]] = [next[j]!, next[i]!];
    void persist(next, "Urutan kelompok video disimpan", "Pengguna melihat kelompok video dalam urutan ini.");
  }

  return (
    <>
      <PageHeader
        divided
        title="E-Learning"
        lead={list.data ? `${formatCount(courses.length)} kelompok video. Video panduan YouTube untuk PA/KPA, PPK, dan Penyedia sesuai peran.` : "Memuat kelompok video"}
        actions={
          <a className="btn" href={adminCourseHref()}>
            <Glyph name="add" />
            <span>Tambah kelompok</span>
          </a>
        }
      />

      {flash ? (
        <Notice tone={flash.tone} title={flash.title}>
          <p>{flash.text}</p>
        </Notice>
      ) : null}

      <section className="region" aria-label="Daftar kelompok video" aria-busy={list.loading || busy || undefined}>
        {list.loading && !list.data ? (
          <SkeletonRows count={3} label="Memuat kelompok video" />
        ) : list.error ? (
          <Notice
            tone="danger"
            title="Daftar kelompok video belum dapat dimuat"
            action={
              <button type="button" className="btn btn-secondary" onClick={list.reload}>
                Muat ulang
              </button>
            }
          >
            <p>{API.errorMessage(list.error)}</p>
          </Notice>
        ) : courses.length === 0 ? (
          <EmptyState
            icon="book"
            title="Belum ada kelompok video"
            body="Tambahkan kelompok pertama, lalu tempel link video YouTube untuk setiap video."
            action={
              <a className="btn" href={adminCourseHref()}>
                Tambah kelompok
              </a>
            }
          />
        ) : (
          <ul className="rows el-list">
            {courses.map((c, i) => (
              <li key={c.id ?? i} className="el-row">
                <div className="el-row-main">
                  <a className="row-title cell-link" href={adminCourseHref(c.id)}>
                    {c.title}
                  </a>
                  <span className="el-row-meta">
                    <RoleBadges roles={c.roles} />
                    <span className="row-meta">
                      <span className="num">{formatCount(c.lessons.length)}</span> video
                    </span>
                  </span>
                </div>
                <div className="row-actions el-row-actions">
                  <button type="button" className="ui-icon-btn" aria-label={`Naikkan ${c.title}`} title="Naikkan" disabled={busy || i === 0} onClick={() => move(i, -1)}>
                    <span className="glyph-up">
                      <Glyph name="chevronRight" />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="ui-icon-btn"
                    aria-label={`Turunkan ${c.title}`}
                    title="Turunkan"
                    disabled={busy || i === courses.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <span className="glyph-down">
                      <Glyph name="chevronRight" />
                    </span>
                  </button>
                  <a className="ui-icon-btn" href={adminCourseHref(c.id)} aria-label={`Ubah ${c.title}`} title="Ubah">
                    <Glyph name="edit2" />
                  </a>
                  <button type="button" className="ui-icon-btn" aria-label={`Hapus ${c.title}`} title="Hapus" disabled={busy} onClick={() => setDeleting(c)}>
                    <Glyph name="trash" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {deleting ? (
        <Dialog
          open
          size="sm"
          busy={busy}
          title={`Hapus kelompok ${deleting.title}?`}
          description={`${formatCount(deleting.lessons.length)} video`}
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setDeleting(null)} disabled={busy}>
                Batal
              </button>
              <button
                type="button"
                className="btn"
                data-tone="danger"
                aria-busy={busy || undefined}
                onClick={async () => {
                  const target = deleting;
                  const ok = await persist(
                    courses.filter((x) => x !== target),
                    `Kelompok ${target.title} dihapus`,
                    "Kelompok ini dan videonya tidak lagi tampil untuk pengguna.",
                  );
                  if (ok) setDeleting(null);
                }}
              >
                <Glyph name="trash" />
                <span>Hapus kelompok</span>
              </button>
            </>
          }
        >
          <div className="confirm-body">
            {flash?.tone === "danger" ? (
              <Notice tone="danger" title={flash.title}>
                <p>{flash.text}</p>
              </Notice>
            ) : null}
            <p className="confirm-text">Kelompok dan semua videonya dihapus dari E-Learning. Tindakan ini tidak dapat dibatalkan.</p>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
